// src/hooks/useUnsavedChanges.js
//
// The one unsaved-changes pattern for every add/edit form (#78,
// docs/briefs/2026-09-28-edit-forms-truth-and-unsaved-warning.md + addendum 1, items 4–5).
//
//   const { isDirty, confirmLeave, markSaved, rebase, dialog } = useUnsavedChanges({ value, ready, onLeave });
//
// - "Dirty" means changed, not filled: `value` (everything the form would save) is compared with
//   what it held when it finished loading (the first render where `ready` is true). A form that
//   already knows what "changed" means passes `dirty` instead of `value`.
// - confirmLeave() → Promise<boolean>: resolves true at once when nothing changed; otherwise asks
//   "leave without saving / keep editing". It fits StandardButton's onCheckUnsaved contract and
//   HeaderButtonsForm's Back button as it is. Render `dialog` once in the form.
// - While dirty, closing or reloading the tab asks the browser's own question (beforeunload), and
//   the browser's Back button asks ours: one history entry with the SAME URL is pushed, so popping
//   it fires no hashchange (App.js turns hash changes into navigation). Leaving calls `onLeave`.
//   Known limitation: on a /demo/<slug> page, App.js's own popstate listener re-runs the demo
//   route check on that pop as well.
// - Inside a FullScreenOverlay, the overlay's close button and backdrop ask through the same check
//   (LeaveGuardContext).
// - markSaved() after a successful save that leaves the form, so leaving afterwards never asks.
//   rebase() after a successful save that keeps the form open: what is on screen becomes the
//   new "saved" state.

import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ConfirmationModal from '../components/core/ConfirmationModal';
import { useTranslation } from '../i18n/useTranslation';

// Provided by FullScreenOverlay: register(guard) → unregister. The guard is () => Promise<boolean>.
export const LeaveGuardContext = createContext(null);

const SENTINEL_KEY = 'ournetUnsavedGuard';
// Only one form at a time owns the Back-button history entry.
let sentinelOwner = null;
let nextId = 1;

// Files are compared by what identifies them; JSON.stringify would turn every File into {}.
export function serialiseForDirty(value) {
  return JSON.stringify(value, (key, v) => {
    if (typeof File !== 'undefined' && v instanceof File) {
      return { file: v.name, size: v.size, modified: v.lastModified };
    }
    if (typeof Blob !== 'undefined' && v instanceof Blob) {
      return { blob: v.size, type: v.type };
    }
    return v;
  });
}

export function useUnsavedChanges({ value, ready = true, dirty = null, onLeave = null }) {
  const { t } = useTranslation('common');
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = nextId++;

  const serialised = dirty === null ? serialiseForDirty(value) : null;
  const [baseline, setBaseline] = useState(null);
  const [saved, setSaved] = useState(false);
  const [asking, setAsking] = useState(false);
  const resolverRef = useRef(null);

  useEffect(() => {
    if (dirty === null && ready && baseline === null) setBaseline(serialised);
  }, [dirty, ready, baseline, serialised]);

  const isDirty = !saved && (dirty !== null
    ? !!dirty
    : (ready && baseline !== null && serialised !== baseline));

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  const confirmLeave = useCallback(() => {
    if (!isDirtyRef.current) return Promise.resolve(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setAsking(true);
    });
  }, []);

  const answer = (leave) => {
    setAsking(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(leave);
  };

  const markSaved = useCallback(() => setSaved(true), []);
  const serialisedRef = useRef(serialised);
  serialisedRef.current = serialised;
  const rebase = useCallback(() => setBaseline(serialisedRef.current), []);

  // Closing or reloading the tab.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // The browser's Back button. One history entry with the SAME URL is pushed the first time the
  // form becomes dirty and kept until the form closes, so a Back press reaches us first:
  // dirty → ask; clean again → leave straight away, as Back would have. On close, if we are still
  // on that entry, it is taken off again (history.back() to the same URL fires no hashchange), so
  // no dead Back press is left behind.
  const ownsEntryRef = useRef(false);
  useEffect(() => {
    if (!isDirty || ownsEntryRef.current || sentinelOwner !== null) return;
    sentinelOwner = idRef.current;
    ownsEntryRef.current = true;
    window.history.pushState({ [SENTINEL_KEY]: idRef.current }, '', window.location.href);
  }, [isDirty]);

  useEffect(() => {
    const id = idRef.current;
    const release = () => {
      ownsEntryRef.current = false;
      if (sentinelOwner === id) sentinelOwner = null;
    };
    const handlePopState = (e) => {
      if (!ownsEntryRef.current) return;
      if (e.state && e.state[SENTINEL_KEY] === id) return; // moved forward onto our own entry
      // Our entry was popped: the member pressed Back.
      if (!isDirtyRef.current) {
        release();
        if (onLeaveRef.current) onLeaveRef.current();
        return;
      }
      confirmLeave().then((leave) => {
        if (leave) {
          release();
          if (onLeaveRef.current) onLeaveRef.current();
        } else {
          window.history.pushState({ [SENTINEL_KEY]: id }, '', window.location.href);
        }
      });
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (ownsEntryRef.current) {
        const stillOnOurEntry = window.history.state && window.history.state[SENTINEL_KEY] === id;
        release();
        if (stillOnOurEntry) window.history.back();
      }
    };
  }, [confirmLeave]);

  // FullScreenOverlay's close button and backdrop.
  const leaveGuard = useContext(LeaveGuardContext);
  useEffect(() => {
    if (!leaveGuard) return undefined;
    return leaveGuard.register(confirmLeave);
  }, [leaveGuard, confirmLeave]);

  const dialog = createElement(ConfirmationModal, {
    visible: asking,
    title: t('unsaved_changes_title'),
    message: t('unsaved_changes_message'),
    okLabel: t('leave_without_saving'),
    cancelLabel: t('keep_editing'),
    danger: true,
    onOk: () => answer(true),
    onCancel: () => answer(false),
  });

  return { isDirty, confirmLeave, markSaved, rebase, dialog };
}
